import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useColorScheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { usePrefs, ACCENTS } from '../../services/prefs';
import { IconSprite, Ic, BrandLogo } from './Icons';
import OrgSwitcher from './OrgSwitcher';
import NotificationBell from './NotificationBell';
import ConnectionStatusDot from './ConnectionStatusDot';
import GlobalSearchBox from './GlobalSearchBox';
import CommandPalette from './CommandPalette';
import ShortcutsCheatsheet from './ShortcutsCheatsheet';
import { shouldSuppressTreeShortcut } from '../../utils/keyboardGuards';
import { navClickHandler } from '../../utils/navClick';

// Sidebar nav — mirrors the agreed mockup (mockup.html / shell.js), mapped to
// the app's real routes.
const NAV = [
  { icon: 'tree', label: 'Requirements', path: '/' },
  { icon: 'grid', label: 'Dashboard', path: '/analytics' },
  { icon: 'nodes', label: 'Vector space', path: '/vector-space' },
  { icon: 'import', label: 'Document import', path: '/document-import' },
  { icon: 'doc', label: 'Demo data', path: '/examples' },
  { group: 'Organisation' },
  { icon: 'users', label: 'Organisation', path: '/account/org' },
  { icon: 'shield', label: 'Roles', path: '/account/roles' },
  // Platform console is super-admin only (gated in the render below).
  { icon: 'shield', label: 'Admin', path: '/account/platform', superAdminOnly: true },
  { spacer: true },
  { icon: 'bell', label: 'Notifications', path: '/account/notifications' },
  { icon: 'gear', label: 'Settings', path: '/account/settings' },
];

// Mobile bottom nav (mockup): the four primary destinations.
const BOTTOM_NAV = [
  { icon: 'tree', label: 'Requirements', path: '/' },
  { icon: 'grid', label: 'Dashboard', path: '/analytics' },
  { icon: 'import', label: 'Import', path: '/document-import' },
  { icon: 'gear', label: 'Settings', path: '/account/settings' },
];

const sectionFor = (pathname) => {
  const hit = NAV.find((n) => n.path && (n.path === '/' ? pathname === '/' : pathname.startsWith(n.path)));
  return hit ? hit.label : 'Requirements';
};

export default function AppShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, systemMode, setMode } = useColorScheme();
  const { user, activeOrg, logout, isSuperAdmin, orgs } = useAuth();
  const prefs = usePrefs();
  const [accountMenu, setAccountMenu] = React.useState(false);
  const [navOpen, setNavOpen] = React.useState(false); // mobile sidebar drawer
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false); // mobile top-bar search overlay
  // #150: command palette (⌘K) + its "?" shortcuts cheatsheet.
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [cheatsheetOpen, setCheatsheetOpen] = React.useState(false);
  // Close the mobile drawer whenever the route changes.
  React.useEffect(() => { setNavOpen(false); setMobileSearchOpen(false); }, [location.pathname]);
  // Selecting a sidebar item while an item is open (mobile): item selection is
  // a URL-hash overlay on top of '/' (see useTreeState), not its own route, so
  // navigate('/') from '/' is a no-op and the detail overlay would stay up,
  // blocking the tree underneath. Clearing the hash reveals it (the same
  // mechanism the detail page's own "Back" button uses) — regression fix for
  // the mobile hamburger menu (#238 follow-up): tapping Requirements now
  // actually shows the tree again instead of doing nothing.
  const go = (p) => {
    setNavOpen(false);
    if (p === '/') {
      // #373: if an item is open on mobile, DON'T clear its hash (which would
      // deselect it and blank the detail). Instead ask the tree to come up as an
      // overlay floating over the item's detail, so hiding the tree returns to the
      // requirement the user was viewing. On desktop the tree is a docked column,
      // so keep the original behaviour: clear the hash to reveal it underneath.
      // Guard `matchMedia` — jsdom (tests) and any non-browser host leave it
      // undefined; an unguarded call there throws and would abort the nav. Absent
      // matchMedia we fall back to the desktop path (clear the hash).
      const isMobile = typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 640px)').matches;
      const asOverlay = isMobile && !!window.location.hash;
      if (!asOverlay && window.location.hash) window.location.hash = '';
      // #314: the tree's own "hide" button sets local, in-component state
      // (`treeVisible`) that a same-route nav click can't reach via `navigate` —
      // React Router no-ops on navigating to the route you're already on, so
      // RequirementsTreeView never remounts and stays hidden. Tapping
      // Requirements (bottom nav or hamburger) while already there is exactly
      // the "unhide" gesture a user would expect, so broadcast it explicitly.
      window.dispatchEvent(new CustomEvent('rmdv:show-tree', { detail: { asOverlay } }));
    }
    navigate(p);
  };

  // #150: global keyboard-shortcut layer.
  // - ⌘K / Ctrl+K — open the command palette (works even while typing, like most
  //   apps' palette shortcut — it's the one binding that should always fire).
  // - / — focus the desktop search box (suppressed while typing/modal open, same
  //   guard the tree's own shortcuts use, so it can't hijack a text field).
  // - ? — open the shortcuts cheatsheet (same suppression).
  React.useEffect(() => {
    const onKeyDown = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (shouldSuppressTreeShortcut()) return;
      if (e.key === '/') {
        e.preventDefault();
        document.querySelector('[data-testid="global-search"]')?.focus();
      } else if (e.key === '?') {
        e.preventDefault();
        setCheatsheetOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // #793 H / #149: Escape dismisses the account menu and the mobile nav drawer.
  // Both were POINTER-ONLY dismissible — you clicked the scrim behind them — which
  // is what made those scrims `<div onClick>` a11y errors. Marking a scrim
  // `aria-hidden` (it is decorative chrome, not a control) is only honest once the
  // keyboard has its own way out, so the two land together.
  React.useEffect(() => {
    if (!accountMenu && !navOpen) return undefined;
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      setAccountMenu(false);
      setNavOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [accountMenu, navOpen]);

  const resolved = (mode === 'system' ? systemMode : mode) || 'light';
  // Accent override only when the user picked a non-default; azure is the brand
  // default and dark mode keeps its own indigo accent unless explicitly chosen.
  const accentVar = (prefs.accent && prefs.accent !== 'azure') ? { '--accent': ACCENTS[prefs.accent] } : null;
  const isActive = (p) => (p === '/' ? location.pathname === '/' : location.pathname.startsWith(p));

  const email = user?.email || '';
  const displayName = user?.display_name || email.split('@')[0] || 'Account';
  const initials = (displayName.match(/\b\w/g) || ['?']).slice(0, 2).join('').toUpperCase();
  const orgName = (user?.orgs || []).find((o) => o.id === activeOrg)?.name || 'RMDV';
  const section = sectionFor(location.pathname);
  // #250: once a user belongs to more than one org (mostly super-admins), the
  // breadcrumb's plain org-name text is replaced by the interactive OrgSwitcher
  // (which renders nothing itself for a single-org user) so there's always a
  // clear indication of — and a shortcut to change — which org is active.
  const hasMultipleOrgs = (orgs || []).length > 1;

  return (
    <div
      className={`app ${resolved}${navOpen ? ' nav-open' : ''}`}
      data-density={prefs.density}
      data-reduce-motion={prefs.reduceMotion ? 'on' : undefined}
      // #404: height (100vh fallback + 100dvh) is set on the `.app` CSS rule so the
      // shell tracks the dynamic mobile viewport; kept out of this inline style so the
      // vh fallback survives on browsers without dvh support.
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', ...accentVar }}
    >
      <IconSprite />
      <div className="shell">
        {/* TOP BAR */}
        <div className="tbar">
          {mobileSearchOpen ? (
            <div className="msearch-full" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <button type="button"
                className="icon-btn"
                style={{ borderColor: 'transparent', background: 'transparent' }}
                aria-label="Close search"
                onClick={() => setMobileSearchOpen(false)}
              >
                <Ic name="arrow" sm />
              </button>
              <GlobalSearchBox testId="global-search-mobile" focusOnMount fullWidth />
            </div>
          ) : (
            <>
              <button type="button"
                className="icon-btn hamb"
                style={{ borderColor: 'transparent', background: 'transparent' }}
                aria-label="Menu"
                onClick={() => setNavOpen((o) => !o)}
              >
                <Ic name="menu" />
              </button>
              {/* #402: a compact file-tree icon summons the requirements tree on mobile,
                  between the hamburger and the logo — replaces the old full-width "Tree"
                  row that wasted a whole row of vertical space. Mobile-only via `.treenav`. */}
              <button type="button"
                className="icon-btn treenav"
                style={{ borderColor: 'transparent', background: 'transparent' }}
                aria-label="Requirements tree"
                title="Requirements tree"
                onClick={() => go('/')}
              >
                <Ic name="tree" />
              </button>
              {/* #363: clicking the logo toggles the nav menu (mirrors the hamburger). */}
              <span
                className="logo"
                style={{ flexShrink: 0, cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                aria-label="Toggle menu"
                onClick={() => setNavOpen((o) => !o)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setNavOpen((o) => !o);
                  }
                }}
              >
                <BrandLogo size={24} />
                <span className="word">RM<b>DV</b></span>
              </span>
              {hasMultipleOrgs && <OrgSwitcher />}
              <div className="crumb">
                {!hasMultipleOrgs && <span>{orgName}</span>}
                <Ic name="chev" sm />
                <b>{section}</b>
              </div>
              <div className="grow" />
              {/* Traditional inline search: an input with a results dropdown anchored under it
                  (no backdrop) — Text/Vector toggle, id/type/project badges, hover-preview.
                  ⌘K still opens the full command-palette MODAL (with Go-to/actions). */}
              <GlobalSearchBox testId="global-search" showShortcut />
              <button type="button"
                className="icon-btn msearch-btn"
                style={{ borderColor: 'transparent', background: 'transparent' }}
                aria-label="Search"
                onClick={() => setMobileSearchOpen(true)}
              >
                <Ic name="search" />
              </button>
              <button type="button"
                className="icon-btn"
                style={{ borderColor: 'transparent', background: 'transparent' }}
                title={resolved === 'dark' ? 'Light mode' : 'Dark mode'}
                onClick={() => setMode(resolved === 'dark' ? 'light' : 'dark')}
              >
                <Ic name={resolved === 'dark' ? 'sun' : 'moon'} />
              </button>
              {/* #161: backend-connected heartbeat status dot — distinct from
                  the browser's navigator.onLine (#160). */}
              <ConnectionStatusDot />
              <NotificationBell />
              <button type="button"
                className="icon-btn"
                style={{ borderColor: 'transparent', background: 'transparent' }}
                title="Keyboard shortcuts (?)"
                onClick={() => setCheatsheetOpen(true)}
              >
                <Ic name="help" />
              </button>
            </>
          )}
          {!mobileSearchOpen && (
          <div style={{ position: 'relative' }}>
              <button type="button"
                className="avatar"
                aria-label="Account"
                aria-haspopup="menu"
                title={email}
                onClick={() => setAccountMenu((o) => !o)}
                style={{ border: 'none', cursor: 'pointer', font: 'inherit' }}
              >
                {initials}
              </button>
              {accountMenu && (
                <>
                  {/* #793 H: decorative click-away catcher. Keyboard dismissal is
                      Escape (effect above) or re-activating the Account button. */}
                  <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} aria-hidden="true" onClick={() => setAccountMenu(false)} />
                  <div
                    role="menu"
                    className="cardp"
                    style={{ position: 'absolute', top: 38, right: 0, zIndex: 41, minWidth: 200, padding: 6 }}
                  >
                    <div className="t-over" style={{ padding: '4px 8px' }}>{email}</div>
                    <a role="menuitem" href="/getting-started" className="snav" style={{ width: '100%', textAlign: 'left' }}
                      onClick={navClickHandler(() => { setAccountMenu(false); navigate('/getting-started'); })}>
                      <Ic name="spark" /> Getting started
                    </a>
                    <a role="menuitem" href="/account/settings" className="snav" style={{ width: '100%', textAlign: 'left' }}
                      onClick={navClickHandler(() => { setAccountMenu(false); navigate('/account/settings'); })}>
                      <Ic name="gear" /> My account
                    </a>
                    <a role="menuitem" href="/account/org" className="snav" style={{ width: '100%', textAlign: 'left' }}
                      onClick={navClickHandler(() => { setAccountMenu(false); navigate('/account/org'); })}>
                      <Ic name="users" /> Organisation
                    </a>
                    {/* Sign out is an ACTION (logout()), not a navigation destination — no
                        URL to open in a new tab, so it stays a real <button>. */}
                    <button type="button" role="menuitem" className="snav" style={{ width: '100%', textAlign: 'left' }}
                      onClick={() => { setAccountMenu(false); logout(); }}>
                      <Ic name="key" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* BODY: sidebar + content */}
        <div className="body">
          {/* #793 H: mobile off-canvas nav scrim — decorative; Escape closes (above). */}
          {navOpen && <div className="scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}
          <nav className="sbar">
            {/* NAV is a module-level constant (never reordered/filtered at runtime),
                so each entry's own path/group/spacer marker is a stable key — no
                two entries share one. */}
            {NAV.map((it) => {
              const navKey = it.path || it.group || 'nav-spacer';
              if (it.group) return <div key={navKey} className="t-over sgrp">{it.group}</div>;
              if (it.spacer) return <div key={navKey} className="sspace" />;
              if (it.superAdminOnly && !isSuperAdmin) return null;
              return (
                <a
                  key={navKey}
                  href={it.path}
                  className={`snav${isActive(it.path) ? ' active' : ''}`}
                  onClick={navClickHandler(() => go(it.path))}
                >
                  <Ic name={it.icon} /> {it.label}
                </a>
              );
            })}
          </nav>
          <main style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
            {children}
          </main>
        </div>
      </div>

      {/* MOBILE bottom nav (shown < 640px via screens.css) */}
      <nav className="bottomnav">
        {BOTTOM_NAV.map((it) => (
          <a
            key={it.path}
            href={it.path}
            className={`bn${isActive(it.path) ? ' active' : ''}`}
            onClick={navClickHandler(() => go(it.path))}
          >
            <Ic name={it.icon} /> {it.label}
          </a>
        ))}
      </nav>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutsCheatsheet open={cheatsheetOpen} onClose={() => setCheatsheetOpen(false)} />
    </div>
  );
}
